import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { AppUpdate } from '@capawesome/capacitor-app-update';

import { Button, Dialog, DialogActions, DialogContent, Stack, Typography } from '@mui/material';
import { MIcon } from '../../@material-extend';
import { IconApp } from '../../../assets/icons';
import OpenStoreButton from './OpenStoreButton';

import { useBreakpoints } from '../../../hooks/useBreakpoints';

/**
 * Provides a modal dialog to prompt the user to download the app or update it from the app store.
 * It checks for available updates and informs the user if an update is necessary.
 *
 * @param {Object} props - Component props.
 * @param {boolean} props.open - Determines if the dialog should be open or closed.
 * @param {Function} props.onClose - Function to call when closing the dialog.
 * @returns {JSX.Element|null} A modal dialog or null if not opened.
 */
const DownloadAppModal = ({ open, onClose }) => {
  const { isApp } = window.env;
  const { isTablet } = useBreakpoints();
  const [updateAvailable, setUpdateAvailable] = useState(false);

  // Effect to check for available app updates on component mount
  useEffect(() => {
    const checkForUpdates = async () => {
      AppUpdate.getAppUpdateInfo()
        .then((updateInfo) => {
          const { updateAvailability } = updateInfo;
          setUpdateAvailable(updateAvailability === 2); // UPDATE_AVAILABLE
        })
        .catch(console.error);
    };

    if (open && isApp) {
      checkForUpdates();
    }
  }, [open]);

  if (!open) {
    return;
  }

  return (
    <Dialog
      fullWidth
      aria-labelledby="dialog-download-on-app-store"
      fullScreen={isTablet}
      open={open}
      onClose={onClose}
    >
      <DialogContent>
        <Stack height="100%" alignItems="center" justifyContent="center" gap={6} sx={{ my: 4 }}>
          <Typography variant="h2" align="center">
            Comète Link
          </Typography>

          <Stack alignItems="center" gap={2}>
            <IconApp sx={{ height: 128, width: 'auto', mb: 2 }} />
            {/* <LogoCompany sx={{ height: 128, width: 'auto', mb: 2 }} /> */}

            <Typography variant="h3" align="center">
              Téléchargez {updateAvailable ? 'la mise à jour' : "l'application"}
            </Typography>

            <Typography variant="h5" align="center" color="textSecondary" gutterBottom>
              {updateAvailable
                ? "Pour continuer à utiliser votre application dans les meilleures conditions, nous vous recommandons d'effectuer la mise à jour."
                : 'Restez connecté à votre solution de sécurité privée, où que vous soyez.'}
            </Typography>
          </Stack>

          <OpenStoreButton />
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button variant="link" endIcon={<MIcon icon="arrow_forward" />} onClick={onClose}>
          {updateAvailable ? 'Rester sur cette version' : 'Continuer sur le site'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

DownloadAppModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func
};

export default DownloadAppModal;
